import { isNumericTableValue } from './markdownTable';

export type MarkdownHastNode = {
  children?: MarkdownHastNode[];
  properties?: Record<string, unknown>;
  tagName?: string;
  type: string;
  value?: string;
};

/**
 * Right-aligns a table column only when every body cell in it is numeric, so
 * mixed columns keep the default left alignment. Alignment is a column-level
 * decision, so it is applied here on the tree rather than per rendered cell.
 */
export function rehypeNumericTableCells(numericClassName: string) {
  return () => (tree: MarkdownHastNode) => {
    forEachTable(tree, (table) => {
      const numericColumns = numericTableColumns(table);

      for (const row of tableRows(table)) {
        for (const [columnIndex, cell] of tableRowCells(row).entries()) {
          if (numericColumns[columnIndex]) {
            addClassName(cell, numericClassName);
          }
        }
      }
    });
  };
}

function numericTableColumns(table: MarkdownHastNode): boolean[] {
  const bodyRows = tableRows(table).filter((row) =>
    tableRowCells(row).some((cell) => cell.tagName === 'td')
  );
  if (bodyRows.length === 0) {
    return [];
  }

  const columnCount = Math.max(
    ...bodyRows.map((row) => tableRowCells(row).length)
  );

  return Array.from({ length: columnCount }, (_, columnIndex) =>
    bodyRows.every((row) => {
      const cell = tableRowCells(row)[columnIndex];
      return cell != null && isNumericTableValue(hastNodeText(cell));
    })
  );
}

function forEachTable(
  node: MarkdownHastNode,
  visit: (table: MarkdownHastNode) => void
): void {
  if (node.tagName === 'table') {
    visit(node);
    return;
  }
  for (const child of node.children ?? []) {
    forEachTable(child, visit);
  }
}

function tableRows(table: MarkdownHastNode): MarkdownHastNode[] {
  const sections = ['thead', 'tbody', 'tfoot'].flatMap((tagName) =>
    childElements(table, tagName)
  );
  return [table, ...sections].flatMap((section) =>
    childElements(section, 'tr')
  );
}

function tableRowCells(row: MarkdownHastNode): MarkdownHastNode[] {
  return (row.children ?? []).filter(
    (cell) => cell.tagName === 'th' || cell.tagName === 'td'
  );
}

function childElements(
  node: MarkdownHastNode,
  tagName: string
): MarkdownHastNode[] {
  return (node.children ?? []).filter((child) => child.tagName === tagName);
}

function hastNodeText(node: MarkdownHastNode): string {
  return node.type === 'text'
    ? node.value ?? ''
    : (node.children ?? []).map((child) => hastNodeText(child)).join('');
}

function addClassName(node: MarkdownHastNode, className: string): void {
  const properties = (node.properties ??= {});
  const existing = properties['className'];
  properties['className'] = Array.isArray(existing)
    ? [...existing, className]
    : [className];
}
